import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { Prompt } from 'react-router-dom';
import { Container, Card, Alert, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { v4 as uuidv4 } from 'uuid';
import {
  CustomMarkerLibrary,
  CustomMarkerLibraryHeader,
  CustomMarkerDefinitionRow,
  definitionProblemsById,
  JumpToFilterButton,
  libraryFromRows,
  libraryShowsFilter,
  renameInDefinitionRows,
  rowsFromLibrary,
  unsavedMarkers,
} from '../components/settings/custom-marker-library';
import {
  CustomMarkerSections,
  CustomMarkerSectionsHeader,
  markerCountsBySection,
  renameInSections,
  sectionNameHeldElsewhere,
  sectionProblemsById,
  sectionsFromRows,
} from '../components/settings/custom-marker-sections';
import {
  LabPanel,
  markersFromPanel,
  panelSectionName,
} from '../models/LabPanelCatalog';
import { GenericModal } from '../components/common/generic-modal';
import { profileSaveErrorMessage } from '../utils/profileSaveError';
import {
  CUSTOM_MARKER_UPGRADE_MODAL,
  CustomMarkerDefinition,
  CustomMarkerSection,
  practitionerSectionFromOptionValue,
  practitionerSectionKey,
  practitionerSectionOptionValue,
  renameInDefaultTestSections,
  sectionGroupKey,
  withDefaultTestMarker,
  withDefaultTestSection,
  withExpandedTestSection,
} from '../models';
import { UserProfileUIStore } from '../stores/user-profile-ui-store';
import { UserStore } from '../stores/user-store';
import { LoadingStore } from '../stores/loading-store';

interface MyMarkersContainerProps {
  userProfileUIStore?: UserProfileUIStore;
  userStore?: UserStore;
  loadingStore?: LoadingStore;
}

interface MyMarkersContainerState {
  rows: CustomMarkerDefinitionRow[];
  sections: CustomMarkerSection[];
  /** Sections set to load onto every new test (UserProfile.defaultTestSections). */
  defaultSections: string[];
  /** Individual markers set to load onto every new test, by definition id. */
  defaultMarkerIds: string[];
  /** Sections whose markers show their ranges on Add Test Results. */
  expandedSections: string[];
  /** Whether `rows` has been seeded from the profile yet. */
  loadedProfileId?: string;
  /** What the last Save found wrong, rendered against the rows themselves. */
  markerErrors: { [rowId: string]: string[] };
  sectionErrors: { [sectionId: string]: string };
  successMessage: string | null;
  /** Warning rather than success when Save had to hold something back. */
  heldSomethingBack: boolean;
  errorMessage: string | null;
  /** Unsaved edits. Drives the floating Save, which stays out of the way
   *  entirely until there is something to save. */
  dirty: boolean;
  showUpgradeModal: boolean;
  /**
   * Whether each block is folded shut. UI only, and per visit.
   *
   * Offered because the two blocks are read for different reasons: somebody
   * here to rename a section should not have to scroll past a hundred markers
   * to reach the list of them.
   */
  markersCollapsed: boolean;
  sectionsCollapsed: boolean;
}

/**
 * "My Markers" — the practitioner's own marker definitions, on their own page
 * under the Test Results menu.
 *
 * Lived inside Settings originally and got lost among the display toggles; it
 * is a data-management screen, not a preference, so it earns its own page.
 */
@inject('userProfileUIStore', 'userStore', 'loadingStore')
@observer
export class MyMarkersContainer extends React.Component<
  MyMarkersContainerProps,
  MyMarkersContainerState
> {
  state: MyMarkersContainerState = {
    rows: [],
    sections: [],
    defaultSections: [],
    defaultMarkerIds: [],
    expandedSections: [],
    loadedProfileId: undefined,
    markerErrors: {},
    sectionErrors: {},
    successMessage: null,
    heldSomethingBack: false,
    errorMessage: null,
    dirty: false,
    showUpgradeModal: false,
    markersCollapsed: false,
    sectionsCollapsed: false,
  };

  componentDidMount() {
    const { userProfileUIStore, userStore } = this.props;

    if (!userStore?.currentUser?.subject) {
      userStore?.loadCurrentUser();
    }
    if (!userProfileUIStore?.currentUserProfile) {
      userProfileUIStore?.loadCurrentUserProfile();
    }
    // Nothing on this page is autosaved either, and a library edit can be a
    // hundred rows deep. Same pair as Add Test Results: the browser's own
    // warning for closing the tab, the <Prompt> in render for the menu.
    window.addEventListener('beforeunload', this.warnIfUnsaved);
    this.seedRowsFromProfile();
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.warnIfUnsaved);
  }

  /** See BloodworkEntry.warnIfUnsaved: browsers ignore the string. */
  private warnIfUnsaved = (event: BeforeUnloadEvent) => {
    if (!this.state.dirty) {
      return undefined;
    }
    event.preventDefault();
    event.returnValue = '';
    return '';
  };

  componentDidUpdate() {
    this.seedRowsFromProfile();
  }

  /**
   * Copy the stored library into editable rows once the profile arrives.
   *
   * Keyed on the profile id and run only once for it, so an incidental MobX
   * re-render (or the fire-and-forget profile write that "learns" a marker from
   * a saved test) can never wipe out edits in progress on this page.
   */
  private seedRowsFromProfile() {
    const profile = this.props.userProfileUIStore?.currentUserProfile;
    if (!profile || this.state.loadedProfileId === profile.id) {
      return;
    }
    this.setState({
      rows: rowsFromLibrary(profile.customMarkerLibrary),
      sections: (profile.customMarkerSections || []).map(
        section => new CustomMarkerSection(section),
      ),
      defaultSections: [...(profile.defaultTestSections || [])],
      defaultMarkerIds: [...(profile.defaultTestMarkerIds || [])],
      expandedSections: [...(profile.expandedTestSections || [])],
      loadedProfileId: profile.id,
      dirty: false,
    });
  }

  /**
   * A section created from a marker row's Section dropdown. Added to the list
   * being edited rather than written straight to the profile: everything on
   * this page is saved together by the button at the bottom, and a section that
   * appeared in the list while the rest of the page stayed unsaved would be the
   * one thing here that behaved differently.
   */
  handleCreateSection = (name: string) => {
    const trimmed = (name || '').trim();
    if (!trimmed) {
      return;
    }
    const exists = this.state.sections.some(
      section =>
        practitionerSectionKey(section.name) === practitionerSectionKey(trimmed),
    );
    if (exists) {
      return;
    }
    this.setState({
      sections: [
        ...this.state.sections,
        new CustomMarkerSection({ id: uuidv4(), name: trimmed }),
      ],
      dirty: true,
    });
  };

  /**
   * Take a shipped lab panel into the library.
   *
   * Appended to the rows being EDITED, like everything else on this page, so
   * the practitioner reviews what arrived and presses the same Save: a panel
   * taken by mistake is undone by reloading, and a marker they do not want is
   * deleted before it is ever written. Nothing is sent to the profile here.
   *
   * Deduped by name against the page rather than the saved library, because a
   * marker name is unique across the whole library and Save would refuse the
   * second copy. See markersFromPanel.
   */
  handleAddLabPanel = (panel: LabPanel, sectionName: string) => {
    const section = panelSectionName(panel, sectionName);
    const { added } = markersFromPanel(
      panel,
      section,
      this.state.rows.map(row => row.name),
      uuidv4,
    );
    if (!added.length) {
      return;
    }
    /*
     * A panel filing into a SHIPPED section creates nothing. `section` there is
     * the report's own heading, which already exists and is not the
     * practitioner's to own; writing a CustomMarkerSection called "Lipid
     * Balance" would be a named section colliding with a shipped one, which is
     * exactly what `practitionerSectionProblem` refuses when they type it.
     */
    const needsSection = !panel.section;
    const known = this.state.sections.some(
      existing =>
        practitionerSectionKey(existing.name) ===
        practitionerSectionKey(section),
    );
    this.setState(state => ({
      rows: [...state.rows, ...rowsFromLibrary(added)],
      // A section is only created once a marker is actually filed under it, so
      // a panel that added nothing cannot leave an empty section behind.
      sections:
        !needsSection || known || !section
          ? state.sections
          : [
              ...state.sections,
              new CustomMarkerSection({ id: uuidv4(), name: section }),
            ],
      dirty: true,
    }));
  };

  /**
   * Correct a section's name from a marker row's Section dropdown: the row, and
   * every other definition filed under the same section, plus the list above.
   */
  handleRenameSection = (from: string, to: string) => {
    this.setState({
      sections: renameInSections(this.state.sections, from, to, uuidv4),
      rows: renameInDefinitionRows(this.state.rows, from, to),
      // The defaults list stores the NAME, so a rename that skipped it would
      // quietly stop the panel loading onto new tests.
      defaultSections: renameInDefaultTestSections(
        this.state.defaultSections,
        from,
        to,
      ),
      expandedSections: renameInDefaultTestSections(
        this.state.expandedSections,
        from,
        to,
      ),
      dirty: true,
    });
  };

  /**
   * Turn "add to every new test" on or off for one section.
   *
   * Not saved on the click: everything on this page commits together, and a
   * setting that wrote itself the moment it was ticked would be the one control
   * here that behaved differently from the rows around it.
   */
  /**
   * Turn "add to every new test" on or off for ONE marker.
   *
   * Independent of its section's own setting: unticking a section must not
   * silently clear the markers somebody picked individually inside it, and a
   * marker ticked here keeps loading if the section is later unticked.
   */
  handleToggleDefaultMarker = (definitionId: string, on: boolean) => {
    this.setState(state => ({
      defaultMarkerIds: withDefaultTestMarker(
        state.defaultMarkerIds,
        definitionId,
        on,
      ),
      dirty: true,
    }));
  };

  /** Show or fold this section's ranges on Add Test Results. */
  handleToggleExpandedSection = (sectionValue: string, on: boolean) => {
    this.setState(state => ({
      expandedSections: withExpandedTestSection(
        state.expandedSections,
        sectionValue,
        on,
      ),
      dirty: true,
    }));
  };

  handleToggleDefaultSection = (sectionValue: string, on: boolean) => {
    this.setState(state => ({
      defaultSections: withDefaultTestSection(
        state.defaultSections,
        sectionValue,
        on,
      ),
      dirty: true,
    }));
  };

  /**
   * Remove a section, and put its markers where the practitioner said to.
   *
   * The trash used to drop the entry from this list and stop there, which read
   * as a delete that did not work: groups in My Markers are built from the
   * markers, so the heading came straight back with its markers under it and
   * only the row in My Sections went. Neither answer could be assumed, so the
   * list asks first and this handler carries out whichever was chosen.
   *
   * 'move' re-files them under Other Lab Results (the empty section
   * value), which is where a marker with no section of its own already prints.
   * 'delete' takes the definitions out of the library with the section.
   */
  handleRemoveSection = (
    section: CustomMarkerSection,
    markers: 'move' | 'delete',
  ) => {
    const key = practitionerSectionKey(section.name);
    // A section still being named has no markers under it — and a blank key
    // would match every marker sitting in Other Lab Results.
    //
    // Nothing happens to the markers when ANOTHER entry with this same name is
    // left standing (see sectionNameHeldElsewhere): the duplicate is a stray row
    // in the list, and removing it must not disturb a section that still exists.
    // Without this, deleting a name typed twice by mistake reported the
    // original's marker count and offered to delete the original's markers.
    const survives = sectionNameHeldElsewhere(
      this.state.sections,
      section.id,
      section.name,
    );
    const filed = (row: CustomMarkerDefinitionRow) =>
      !!key &&
      !survives &&
      practitionerSectionKey(
        practitionerSectionFromOptionValue(row.section),
      ) === key;

    const rows =
      markers === 'delete'
        ? this.state.rows.filter(row => !filed(row))
        : this.state.rows.map(row =>
            filed(row) ? { ...row, section: '' } : row,
          );

    // What Save said about a row that has just been deleted has nothing left to
    // render against, and a moved row's message was never about its section.
    const markerErrors = { ...this.state.markerErrors };
    const live = new Set(rows.map(row => row.id));
    Object.keys(markerErrors).forEach(id => {
      if (!live.has(id)) {
        delete markerErrors[id];
      }
    });
    const sectionErrors = { ...this.state.sectionErrors };
    delete sectionErrors[section.id];

    // The per-section settings store the NAME, so both have to go with it or a
    // section named the same thing later inherits them. A row nobody has named
    // yet is skipped rather than matched: a blank name groups as 'own', which
    // is Other Lab Results, so deleting an empty row would take that
    // section's own two settings with it.
    // Same rule for the two per-section settings: the name is still in use, so
    // "add to every test" and "show ranges" still have a section to be about.
    const gone =
      key && !survives
        ? sectionGroupKey(practitionerSectionOptionValue(section.name))
        : undefined;
    const stillThere = (value: string) =>
      !gone || sectionGroupKey(value) !== gone;

    this.setState({
      sections: this.state.sections.filter(entry => entry.id !== section.id),
      rows,
      defaultSections: this.state.defaultSections.filter(stillThere),
      expandedSections: this.state.expandedSections.filter(stillThere),
      // Ids of deleted definitions have nothing to resolve to. Save drops them
      // as well, but leaving them here would tick a marker that is gone.
      defaultMarkerIds: this.state.defaultMarkerIds.filter(id => live.has(id)),
      markerErrors,
      sectionErrors,
      dirty: true,
    });
  };

  /**
   * The section list changed. Renaming an entry there has to carry the marker
   * definitions filed under it along, or they would be left pointing at a name
   * that no longer exists and would quietly reappear in the dropdown as a
   * section of their own.
   *
   * Nothing moves while another entry in the list is called the same thing. A
   * name is typed one character at a time, so a NEW section on its way to
   * "Urinalysis Organic Acids" IS "Urinalysis" for a keystroke — and markers are
   * matched by name, so the next character used to rewrite the existing
   * Urinalysis panel's whole marker list into the row being typed. Its 38
   * markers moved to a section the practitioner was still naming and the section
   * they belonged to read empty. This is the rule the trash already applies with
   * `survives`: while two entries answer to one name, the markers stay where
   * they are.
   */
  handleSectionsChange = (nextSections: CustomMarkerSection[]) => {
    let rows = this.state.rows;
    let defaultSections = this.state.defaultSections;
    let expandedSections = this.state.expandedSections;
    const sectionErrors = { ...this.state.sectionErrors };
    nextSections.forEach((next, index) => {
      const before = this.state.sections.find(
        section => section.id === next.id,
      );
      if (
        before &&
        (before.name || '').trim() &&
        (next.name || '').trim() &&
        practitionerSectionKey(before.name) !==
          practitionerSectionKey(next.name) &&
        // Asked of the list as it was, where this entry still has the name
        // being moved away from.
        !sectionNameHeldElsewhere(this.state.sections, next.id, before.name)
      ) {
        rows = renameInDefinitionRows(rows, before.name, next.name);
        defaultSections = renameInDefaultTestSections(
          defaultSections,
          before.name,
          next.name,
        );
        expandedSections = renameInDefaultTestSections(
          expandedSections,
          before.name,
          next.name,
        );
      }
      if (this.edited(this.state.sections[index], next)) {
        delete sectionErrors[next.id];
      }
    });
    // A section removed from the list while markers are still filed under it
    // keeps its heading (groups are built from the markers), so its "add to
    // every new test" box stays visible and untickable. One removed with
    // nothing left under it has no heading and no way back, so its default
    // would sit in the profile forever with nothing to resolve to.
    const surviving = new Set(
      [
        ...nextSections.map(section =>
          practitionerSectionOptionValue(section.name),
        ),
        ...rows.map(row => row.section),
      ].map(value => sectionGroupKey(value)),
    );
    defaultSections = defaultSections.filter(value =>
      surviving.has(sectionGroupKey(value)),
    );
    // Same rule for the other per-section setting, or a name reused later
    // silently inherits "show ranges" from a section that no longer exists.
    expandedSections = expandedSections.filter(value =>
      surviving.has(sectionGroupKey(value)),
    );

    this.setState({
      sections: nextSections,
      rows,
      defaultSections,
      expandedSections,
      sectionErrors,
      dirty: true,
    });
  };

  /**
   * Whether this is the row that just changed. The editors rebuild only the
   * object being typed into, so identity is exact — and the id has to match too,
   * or removing a row would read as an edit to whatever slid up into its place.
   */
  private edited = (
    before: { id: string } | undefined,
    after: { id: string },
  ): boolean => !!before && before.id === after.id && before !== after;

  /**
   * Whether a marker row changed in a way that could answer what Save said
   * about it. Opening a marker's editor is component state now and rebuilds
   * nothing, but the fields are still compared one by one rather than by
   * identity: `update` carries the untouched fields straight over, and a range
   * set is only ever rebuilt by typing in one, so this stays exact while
   * refusing to call a no-op edit an edit.
   */
  private editedContent = (
    before: CustomMarkerDefinitionRow | undefined,
    after: CustomMarkerDefinitionRow,
  ): boolean =>
    this.edited(before, after) &&
    (before!.name !== after.name ||
      before!.units !== after.units ||
      before!.chartType !== after.chartType ||
      before!.section !== after.section ||
      before!.ranges !== after.ranges ||
      before!.female !== after.female ||
      before!.male !== after.male);

  /**
   * Marker rows changed. A message from the last Save is about the row as it
   * was, so it comes off the moment that row is really edited: red text still
   * sitting under a row the practitioner has just fixed reads as a rule they
   * cannot satisfy. The other rows keep theirs.
   */
  handleRowsChange = (nextRows: CustomMarkerDefinitionRow[]) => {
    const markerErrors = { ...this.state.markerErrors };
    nextRows.forEach((row, index) => {
      if (this.editedContent(this.state.rows[index], row)) {
        delete markerErrors[row.id];
      }
    });
    this.setState({ rows: nextRows, markerErrors, dirty: true });
  };

  /**
   * The library to write, with the rows that do not validate held back.
   *
   * A row that fails keeps whatever the profile already has for it, so a
   * half-typed edit to a marker that was saved and working can never delete it.
   * A row that fails and was never saved (a new one) is simply not written.
   * Order follows the page, so nothing jumps around after a save.
   *
   * Renaming one marker onto another's name flags the second row only, so the
   * library can briefly hold two definitions under one name: the first writes
   * its new name, the second holds at the old one. Left alone deliberately.
   * Nothing reads the library by name (the pick list is by id, and learning a
   * marker from a test only asks whether a name is already known), and it heals
   * itself the moment the flagged row saves.
   */
  private libraryHoldingBack(
    invalid: Set<string>,
    invalidSections: Set<string>,
  ): CustomMarkerDefinition[] {
    const saved = new Map<string, CustomMarkerDefinition>(
      (
        this.props.userProfileUIStore?.currentUserProfile
          ?.customMarkerLibrary || []
      ).map(definition => [definition.id, definition] as [string, CustomMarkerDefinition]),
    );

    const library: CustomMarkerDefinition[] = [];
    this.state.rows.forEach(row => {
      // A row filed under a section Save is refusing goes back with it, name
      // and all. Renaming a section rewrites it onto every marker filed there
      // as it is typed, so writing those rows while holding the section back is
      // how "Thyroid Function" ends up on a definition through the one field
      // that exists to refuse it.
      const sectionKey = practitionerSectionKey(
        practitionerSectionFromOptionValue(row.section),
      );
      if (invalid.has(row.id) || (sectionKey && invalidSections.has(sectionKey))) {
        const previous = saved.get(row.id);
        if (previous) {
          library.push(previous);
        }
        return;
      }
      // Through the shared conversion one row at a time, so a row nobody typed
      // into is dropped here exactly as it always was.
      libraryFromRows([row]).forEach(definition => library.push(definition));
    });
    return library;
  }

  /** The same rule for the section list above the markers. */
  private sectionsHoldingBack(invalid: Set<string>): CustomMarkerSection[] {
    const saved = new Map<string, CustomMarkerSection>(
      (
        this.props.userProfileUIStore?.currentUserProfile
          ?.customMarkerSections || []
      ).map(section => [section.id, section] as [string, CustomMarkerSection]),
    );

    const sections: CustomMarkerSection[] = [];
    this.state.sections.forEach(section => {
      if (invalid.has(section.id)) {
        const previous = saved.get(section.id);
        if (previous) {
          sections.push(previous);
        }
        return;
      }
      sectionsFromRows([section]).forEach(next => sections.push(next));
    });
    return sections;
  }

  /**
   * Save the page.
   *
   * One button, and a row that does not validate no longer stops the rest of
   * the page from being saved: it is held back at whatever was last saved for
   * it, marked in red where it sits, and everything valid is committed around
   * it. The old all-or-nothing rule is what a second "Save Sections" button
   * existed to route around, and two buttons with two scopes is how a
   * practitioner ends up being told "Sections saved." with their marker edit
   * still uncommitted.
   */
  handleSave = async () => {
    const { userProfileUIStore } = this.props;
    if (!userProfileUIStore?.currentUserProfile) {
      return;
    }

    // Where the page is standing before Save changes anything under it.
    const anchor = this.viewportAnchor();

    const sectionErrors = sectionProblemsById(this.state.sections);
    const markerErrors = definitionProblemsById(this.state.rows);
    const badSections = Object.keys(sectionErrors);
    const badMarkers = Object.keys(markerErrors);
    const held = [
      badMarkers.length
        ? `${badMarkers.length} marker${badMarkers.length === 1 ? '' : 's'}`
        : '',
      badSections.length
        ? `${badSections.length} section${badSections.length === 1 ? '' : 's'}`
        : '',
    ].filter(Boolean);

    // Marked up front rather than after the round trip: what is wrong with a
    // row is known the moment Save is pressed, and waiting for the write to
    // come back before saying so is a second of nothing happening.
    this.setState({ markerErrors, sectionErrors }, () => {
      if (held.length) {
        this.showFirstProblem();
      } else {
        // Clearing what the LAST Save marked in red takes those lines out of
        // the list, which moves everything below them.
        this.keepPageStill(anchor);
      }
    });

    // What is going into this write. Typing carries on during it, and an edit
    // made while it is in flight is in neither the write nor the error list —
    // so it has to still count as unsaved when the write lands.
    const written = {
      rows: this.state.rows,
      sections: this.state.sections,
      defaultSections: this.state.defaultSections,
      defaultMarkerIds: this.state.defaultMarkerIds,
      expandedSections: this.state.expandedSections,
    };

    // What the profile held before this write. The update* actions below mutate
    // the observable profile in place, and the save that commits them comes
    // after — so a write that fails used to leave every other screen reading a
    // library that is not in the database. The entry form's picker, the
    // defaults loaded onto the next test and My Markers itself all showed the
    // new state while the only record of the failure was one dismissible
    // alert, until a reload silently put it all back.
    const profile = userProfileUIStore.currentUserProfile;
    const previous = {
      library: profile.customMarkerLibrary,
      sections: profile.customMarkerSections,
      defaultSections: profile.defaultTestSections,
      defaultMarkerIds: profile.defaultTestMarkerIds,
      expandedSections: profile.expandedTestSections,
    };

    try {
      userProfileUIStore.updateCustomMarkerLibrary(
        this.libraryHoldingBack(
          new Set(badMarkers),
          new Set(
            this.state.sections
              .filter(section => sectionErrors[section.id])
              .map(section => practitionerSectionKey(section.name)),
          ),
        ),
      );
      userProfileUIStore.updateCustomMarkerSections(
        this.sectionsHoldingBack(new Set(badSections)),
      );
      // Nothing to hold back: a default is a tick against a section, so there
      // is no half-typed state it could be in. A tick against a section Save is
      // refusing still writes, and resolves to that section's markers as soon
      // as the section itself saves.
      userProfileUIStore.updateDefaultTestSections(this.state.defaultSections);
      // Ids of rows that no longer exist are dropped: a marker deleted from the
      // library has nothing left to resolve to, and a dead id would sit in the
      // profile forever.
      const liveIds = new Set(this.state.rows.map(row => row.id));
      userProfileUIStore.updateDefaultTestMarkerIds(
        this.state.defaultMarkerIds.filter(id => liveIds.has(id)),
      );
      userProfileUIStore.updateExpandedTestSections(this.state.expandedSections);
      await userProfileUIStore.saveCurrentUserProfile();
      this.setState(
        state => ({
          successMessage: held.length
            ? `Saved everything except ${held.join(' and ')}, marked in red below.`
            : 'My Markers saved.',
          heldSomethingBack: held.length > 0,
          errorMessage: null,
          // Still unsaved work on the page when something was held back, or
          // when the practitioner kept typing while the write was on its way.
          dirty:
            held.length > 0 ||
            state.rows !== written.rows ||
            state.sections !== written.sections ||
            state.defaultSections !== written.defaultSections ||
            state.defaultMarkerIds !== written.defaultMarkerIds ||
            state.expandedSections !== written.expandedSections,
        }),
        // The banner this Save just put at the top of the page pushes the
        // whole list down by its height. Held back rows are the exception:
        // showFirstProblem has already taken the page somewhere deliberate,
        // and putting it back would undo that.
        () => {
          if (!held.length) {
            this.keepPageStill(anchor);
          }
        },
      );
    } catch (error) {
      console.error('Error saving My Markers:', error);
      // Put the profile back the way the database still has it. The page keeps
      // the practitioner's edits in its own `rows`/`sections` state and stays
      // dirty, so nothing they typed is lost — but the rest of the app must go
      // on reading what was actually saved until a write succeeds.
      userProfileUIStore.updateCustomMarkerLibrary(previous.library);
      userProfileUIStore.updateCustomMarkerSections(previous.sections);
      userProfileUIStore.updateDefaultTestSections(previous.defaultSections);
      userProfileUIStore.updateDefaultTestMarkerIds(previous.defaultMarkerIds);
      userProfileUIStore.updateExpandedTestSections(previous.expandedSections);
      this.setState(
        {
          errorMessage: profileSaveErrorMessage(
            error,
            'Error saving My Markers. Please try again.',
          ),
          successMessage: null,
          // The write did not land, so everything on the page is still unsaved.
          dirty: true,
        },
        () => {
          if (!held.length) {
            this.keepPageStill(anchor);
          }
        },
      );
    }
  };

  /**
   * What the practitioner is looking at, as an element and where it sits.
   *
   * Save writes a banner in above the list and takes the last Save's red lines
   * out of it, and both of those move every row below them: press Save on row
   * sixty and the page slides under you, which reads as having been thrown
   * somewhere at random. The topmost row still on screen is what "where you
   * were" means, so it is what gets put back.
   */
  private viewportAnchor(): { element: Element; top: number } | undefined {
    if (typeof document === 'undefined') {
      return undefined;
    }
    // The card itself is the fallback for a page scrolled above its first row.
    const candidates = Array.from(
      document.querySelectorAll(
        '.custom-marker-line, .custom-marker-group, #my-markers-card',
      ),
    );
    const element =
      candidates.find(node => node.getBoundingClientRect().top >= 0) ||
      candidates[0];
    return element
      ? { element, top: element.getBoundingClientRect().top }
      : undefined;
  }

  /**
   * Put that element back where it was, taking the page with it.
   *
   * Called from a setState callback, so it runs after React has written the
   * banner into the DOM and before the browser paints: the row does not visibly
   * move and then move back.
   */
  private keepPageStill(anchor?: { element: Element; top: number }) {
    if (!anchor || typeof window === 'undefined') {
      return;
    }
    // The row may have been the one just deleted, and jsdom (like very old
    // browsers) has no scrollBy.
    if (!document.contains(anchor.element) || typeof window.scrollBy !== 'function') {
      return;
    }
    const moved = anchor.element.getBoundingClientRect().top - anchor.top;
    if (moved) {
      window.scrollBy(0, moved);
    }
  }

  /**
   * Bring the first held-back row into view. Without the old summary list at
   * the bottom of the page, a message rendered against row 60 of a long table
   * is a message nobody sees — and the section list has the same problem in
   * reverse, since it sits at the top of a page the practitioner is working at
   * the bottom of.
   */
  private showFirstProblem = () => {
    const { markerErrors, sectionErrors, rows, sections } = this.state;
    const marker = rows.find(row => markerErrors[row.id]);
    const section = sections.find(entry => sectionErrors[entry.id]);
    const id = marker
      ? `my-marker-row-${marker.id}`
      : section
      ? `my-section-row-${section.id}`
      : undefined;
    if (!id || typeof document === 'undefined') {
      return;
    }
    const element = document.getElementById(id);
    // jsdom has no scrollIntoView, and neither do very old browsers.
    if (element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'center' });
    }
  };

  dismissAlert = (type: 'success' | 'error') => {
    if (type === 'success') {
      this.setState({ successMessage: null });
    } else {
      this.setState({ errorMessage: null });
    }
  };

  /**
   * How many sections Save would write: renamed, added and removed. The
   * markers half is unsavedMarkers(), and the pill says the two together.
   *
   * Markers and sections only. A tick against "Add to every new test" is
   * unsaved work and keeps the pill up, but it is not a marker or a section,
   * and counting it would make the number disagree with what the page shows.
   * The pill falls back to a bare "Save" when a toggle is all there is, so
   * zero here does NOT mean there is nothing to save. `dirty` decides that.
   */
  private unsavedSectionCount(): number {
    const profile = this.props.userProfileUIStore?.currentUserProfile;
    const savedSections = new Map<string, string>(
      (profile?.customMarkerSections || []).map(section => [
        section.id,
        section.name,
      ]),
    );
    const live = new Set(this.state.sections.map(section => section.id));
    const changedSections = this.state.sections.filter(section => {
      const saved = savedSections.get(section.id);
      // "+ Add a section" puts an empty row on the list, the same way the
      // marker table has a blank starter row. Neither is work waiting to be
      // committed until somebody types in it.
      if (saved === undefined && !(section.name || '').trim()) {
        return false;
      }
      return saved !== section.name;
    }).length;
    const removedSections = Array.from(savedSections.keys()).filter(
      id => !live.has(id),
    ).length;
    return changedSections + removedSections;
  }

  render() {
    const { userProfileUIStore } = this.props;
    const {
      rows,
      sections,
      defaultSections,
      defaultMarkerIds,
      expandedSections,
      markerErrors,
      sectionErrors,
      successMessage,
      heldSomethingBack,
      errorMessage,
      dirty,
    } = this.state;

    if (!userProfileUIStore?.currentUserProfile) {
      return (
        <Container className="mt-4">
          <Card>
            <Card.Body>
              <div className="text-center">
                <p>Loading user profile...</p>
              </div>
            </Card.Body>
          </Card>
        </Container>
      );
    }

    // Against the library as the DATABASE holds it. handleSave writes the new
    // one onto the profile before the request goes out and puts the old one
    // back if it fails, so both outcomes land here by themselves: a marker
    // that saved loses its chip, and one whose write failed — or one Save held
    // back over a half-filled range — keeps it, which is the truth in both
    // cases.
    const unsaved = unsavedMarkers(
      rows,
      userProfileUIStore.currentUserProfile.customMarkerLibrary,
    );
    const changes = unsaved.count + this.unsavedSectionCount();
    // There is a filter to jump back to only while the markers block is open
    // and long enough to have drawn one.
    const canJumpToFilter =
      !this.state.markersCollapsed && libraryShowsFilter(rows);

    return (
      // Room at the bottom for the floating Save, which is fixed to the
      // viewport and would otherwise sit on top of the last row's right-hand
      // cells. More of it when the jump button is stacked above the Save,
      // since the pair is that much taller than the pill alone.
      //
      // A card per block, on the page's gray, with the section boxes nested
      // inside — the shape Add Test Results already uses, where a blue card
      // holds blue panel cards. Consistency with the rest of the app is worth
      // more here than the flatter hierarchy a single level would give.
      <Container
        fluid
        className="mt-4"
        style={{ paddingBottom: canJumpToFilter ? '140px' : '90px' }}
      >
        <Prompt
          when={dirty}
          message="My Markers has unsaved changes. Leave the page and they will be lost."
        />
        {successMessage && (
          <Alert
            variant={heldSomethingBack ? 'warning' : 'success'}
            dismissible
            onClose={() => this.dismissAlert('success')}
          >
            <div className="text-center m-2">{successMessage}</div>
          </Alert>
        )}

        {errorMessage && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => this.dismissAlert('error')}
          >
            <div className="text-center m-2">{errorMessage}</div>
          </Alert>
        )}

        {/* Markers first. Sections used to lead because a marker could not be
            filed into one that did not exist yet, but the Section dropdown
            creates them inline now, here and while entering a test. What is
            left in the list below is maintenance, which is not what the page is
            opened for. */}
        {/* Identified so Save can measure where the page stands and put it
            back after the banner above it changes the page's height. */}
        <Card id="my-markers-card">
          {/* Card.Header renders a div, so the click alone leaves this
              collapse reachable by mouse only. Every other fold in this
              feature — the section headings here, in the per-test table and
              inside the picker — is a real <Button>; these two were the
              outliers. */}
          <Card.Header
            className="custom-marker-block-header"
            role="button"
            tabIndex={0}
            aria-expanded={!this.state.markersCollapsed}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.setState(state => ({
                  markersCollapsed: !state.markersCollapsed,
                }));
              }
            }}
            onClick={() =>
              this.setState(state => ({
                markersCollapsed: !state.markersCollapsed,
              }))
            }
          >
            <FontAwesomeIcon
              icon={this.state.markersCollapsed ? 'chevron-right' : 'chevron-down'}
              style={{ fontSize: '11px' }}
            />
            &nbsp;&nbsp;My Markers&nbsp;&nbsp;
            <CustomMarkerLibraryHeader />
          </Card.Header>
          {this.state.markersCollapsed ? null : (
            <Card.Body>
              <CustomMarkerLibrary
                rows={rows}
                onChange={this.handleRowsChange}
                newRowId={() => uuidv4()}
                sections={sections}
                onCreateSection={this.handleCreateSection}
                onRenameSection={this.handleRenameSection}
                rowErrors={markerErrors}
                unsavedRowIds={unsaved.rowIds}
                defaultSections={defaultSections}
                onToggleDefaultSection={this.handleToggleDefaultSection}
                defaultMarkerIds={defaultMarkerIds}
                onToggleDefaultMarker={this.handleToggleDefaultMarker}
                expandedSections={expandedSections}
                onToggleExpandedSection={this.handleToggleExpandedSection}
                customMarkersAllowed={userProfileUIStore.currentUserProfile.canUseCustomMarkers()}
                onUpgradeNeeded={() => this.setState({ showUpgradeModal: true })}
                // UNGATED 2026-10-13, the release that took the prebuilt
                // panels to prod. It was `labPanelsVisible() ?
                // this.handleAddLabPanel : undefined` from 2026-10-07, and that
                // module is gone: the gate was this one prop, because the
                // button AND the picker dialog both render only when it is
                // present.
                onAddLabPanel={this.handleAddLabPanel}
              />
            </Card.Body>
          )}
        </Card>

        <Card>
          <Card.Header
            className="custom-marker-block-header"
            role="button"
            tabIndex={0}
            aria-expanded={!this.state.sectionsCollapsed}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.setState(state => ({
                  sectionsCollapsed: !state.sectionsCollapsed,
                }));
              }
            }}
            onClick={() =>
              this.setState(state => ({
                sectionsCollapsed: !state.sectionsCollapsed,
              }))
            }
          >
            <FontAwesomeIcon
              icon={
                this.state.sectionsCollapsed ? 'chevron-right' : 'chevron-down'
              }
              style={{ fontSize: '11px' }}
            />
            &nbsp;&nbsp;My Sections&nbsp;&nbsp;
            <CustomMarkerSectionsHeader />
          </Card.Header>
          {this.state.sectionsCollapsed ? null : (
            <Card.Body>
              <CustomMarkerSections
                sections={sections}
                onChange={this.handleSectionsChange}
                newSectionId={() => uuidv4()}
                errors={sectionErrors}
                markerCounts={markerCountsBySection(sections, rows)}
                onRemove={this.handleRemoveSection}
              />
            </Card.Body>
          )}
        </Card>

        <div className="mt-3">
          <Button variant="primary" onClick={this.handleSave}>
            Save My Markers
          </Button>
        </div>

        {/* The page saves everything on it at once, and the button that does
            that sits under a marker list that can run to a hundred rows — so
            adding one section meant scrolling past every marker to commit it.
            Same pill as Add Test Results, which has the same problem.

            Only while there is something to save: it is fixed over the table's
            right-hand columns, so a pill that is always there is a pill sitting
            on somebody's Alarm Max. Unsaved work is also the one state where
            "which button do I press" has an answer, since it is then the only
            Save lit on the page. */}
        {dirty ? (
          <button
            onClick={this.handleSave}
            title="Save My Markers"
            id="my-markers-save-pill"
            style={{
              position: 'fixed',
              bottom: '32px',
              right: '32px',
              height: '44px',
              padding: '0 20px',
              borderRadius: '22px',
              backgroundColor: '#766d66',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              zIndex: 1050,
            }}
          >
            {/* What is waiting, not just that something is. A practitioner who
                corrected one range and a practitioner who has half a panel
                open both saw the same word, and the count is the difference
                between "press it" and "go and check first". Bare "Save" when
                the only unsaved thing is a checkbox, which is nothing the
                number could honestly describe. */}
            {changes ? `Save ${changes} change${changes === 1 ? '' : 's'}` : 'Save'}
          </button>
        ) : null}

        {/* Stacked above that Save, and the reason both of this page's floating
            controls are rendered here rather than each inside the block it
            serves: the bottom-right corner is one shared space, and two files
            each placing something in it is how you end up with a Save under a
            circle. The button keeps its own scroll state — see
            JumpToFilterButton — so scrolling never re-renders this page. */}
        {canJumpToFilter ? <JumpToFilterButton /> : null}

        <GenericModal
          show={this.state.showUpgradeModal}
          title={CUSTOM_MARKER_UPGRADE_MODAL.title}
          text={CUSTOM_MARKER_UPGRADE_MODAL.text}
          link={CUSTOM_MARKER_UPGRADE_MODAL.link}
          linkText={CUSTOM_MARKER_UPGRADE_MODAL.linkText}
          additionalLink={CUSTOM_MARKER_UPGRADE_MODAL.additionalLink}
          additionalLinkText={CUSTOM_MARKER_UPGRADE_MODAL.additionalLinkText}
          onHide={() => this.setState({ showUpgradeModal: false })}
        />
      </Container>
    );
  }
}
